/* eslint-disable prettier/prettier */
import { ENV } from '@environment';
import * as Sentry from '@sentry/react';
import { travel } from 'api';
import axios, { AxiosResponse } from 'axios';
import moment from 'moment';
import { Dispatch } from 'redux';
import { RootState } from 'store';

let cancel;
const CancelToken = axios.CancelToken;

import {
  autosSearchRequest,
  autosSearchSuccessful,
  autosSearchError,
  autoResultsRequest,
  autoResultsSuccessful,
  autoResultsError,
  autosClear,
  autosTabFilterSuccessful,
  autoAgenciesRequest,
  autoAgenciesError,
  autoAgenciesSuccessful,
  autoValidateRequest,
  autoValidateSuccess,
  autoDetailsRequest,
  autoDetailsSuccess,
} from 'action-creators/autos';
import { DEFAULT_ORDER, DEFAULT_ORDER_DESC } from 'components/AutoFilters/index';
import { Airport } from 'models/airport';
import { VehicleDetailPayload, VehicleDetailResponse } from 'models/auto/vehicleDetail';
import { VehicleFilterResponse } from 'models/auto/vehicleFilter';
import { VehicleResultsPayload, VehicleResultsResponse, VehicleResultsFilters } from 'models/auto/vehicleResults';
import {
  VehicleSearchPayload,
  VehicleSearchResponse,
  VehiclePlace,
  PickUpSearch,
  VehiclePlaceType,
  DropOffSearch,
} from 'models/auto/vehicleSearch';
import { TravelVehicleValidateResponse, VehicleValidatePayload, VehicleValidateResponse } from 'models/auto/vehicleValidate';
import { AutoSolicitationRequest, VehicleBook, VehicleBookDriver } from 'models/autoSolicitation';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import { City } from 'models/city';
import { Response } from 'models/response';
import { TravelErrorEvent, amplitudelogEvent } from 'services/amplitudeService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { dateToString, setHours } from 'utils/time';

import { daysCount } from './../utils/generic';
import { mapVehicleReservation } from './autos.mapper';

export const mapPointToSearch = (
  point: VehiclePlace | City | Airport,
  date: string,
  time: string,
  isDropOff = false,
): PickUpSearch | DropOffSearch => {
  const type =
    point['latitude'] && point['longitude']
      ? VehiclePlaceType.LatLong
      : 'isAirport' in point && point['isAirport']
        ? VehiclePlaceType.Airport
        : VehiclePlaceType.City;
  let place: PickUpSearch | DropOffSearch = {
    pickUpType: type,
    pickUpDateTime: dateToString(setHours(new Date(date), time)),
  };
  if (isDropOff) {
    place = {
      dropOffType: place.pickUpType,
      dropOffDateTime: place.pickUpDateTime,
    };
  }
  const code = type === VehiclePlaceType.City || type === VehiclePlaceType.Airport ? point.code : null;

  if (type === VehiclePlaceType.LatLong) {
    const { latitude, longitude, countryCode } = point as VehiclePlace;
    place.latitude = latitude;
    place.longitude = longitude;
    place.countryCode = countryCode;
  } else if (code) {
    place.destinationCode = code;
  }
  return place;
};

interface SearchAutosArguments {
  pickUpPoint: VehiclePlace;
  dropOffPoint: VehiclePlace;
  pickUpDate: string;
  pickUpTime: string;
  dropOffDate: string;
  dropOffTime: string;
  traveler?: TravelArrangerUsersAllowed;
  diffDropOff: boolean;
  newSessionToken?: string;
  branchCompany?: GetAllBillingCompanyResponse
}

export const searchAutos = ({
  pickUpPoint,
  dropOffPoint,
  pickUpDate,
  pickUpTime,
  dropOffDate,
  dropOffTime,
  traveler,
  diffDropOff,
  newSessionToken,
  branchCompany,
}: SearchAutosArguments) => async (dispatch: Dispatch, getState: () => RootState) =>
    new Promise(async (resolve, reject) => {
      dispatch(
        autosSearchRequest({
          pickUpPoint,
          dropOffPoint,
          pickUpDate,
          pickUpTime,
          dropOffDate,
          dropOffTime,
          traveler,
          diffDropOff,
          branchCompany
        }),
      );

      const { accountResult } = getState().user;
      if (!accountResult) return;

      const { sessionToken } = accountResult;
      const { currency } = getState().i18n.country;
      const currentLanguage = sessionStorage.getItem('@currentLanguage') || getState().i18n.country.culture;

      try {
        const {
          data: { succeeded, data, errors },
        } = await travel.post<VehicleSearchPayload, AxiosResponse<Response<VehicleSearchResponse>>>('Vehicle/Search', {
          pickUp: mapPointToSearch(pickUpPoint, pickUpDate, pickUpTime) as PickUpSearch,
          dropOff: mapPointToSearch(dropOffPoint, dropOffDate, dropOffTime, true) as DropOffSearch,
          maxResults: 0,
          requestSettings: {
            language: currentLanguage.startsWith('es') ? 'es' : currentLanguage,
          },
          currencyCode: currency || 'BRL',
          sessionToken: newSessionToken ? newSessionToken : sessionToken,
          billingCompanyId: branchCompany?.id
        });
        if (!succeeded) {
          throw errors;
        }

        dispatch(autosSearchSuccessful(data));
        resolve(data);
      } catch (ex) {
        const errors = ex.response?.data?.Errors;

        const event: TravelErrorEvent = {
          SOURCE: 'TRAVEL_HOME',
          RESULTS: 0,
          TRAVEL_PRODUCT_TYPE: 'CAR_RENTAL',
          TRAVEL_FROM: pickUpPoint?.code ?? '',
          TRAVEL_TO: dropOffPoint?.code ?? '',
          TRAVEL_DESTINATION_TYPE: '',
          TRAVEL_TRIP_TYPE: '',
          TRAVEL_ADVANCED_PURCHASE: pickUpDate ? daysCount(new Date(), new Date(pickUpDate)).toString() : '',
          TRAVEL_DATE_FROM: pickUpDate?.toString() ?? '',
          TRAVEL_DATE_TO: dropOffDate?.toString() ?? '',
          TRAVEL_TRIP_LENGTH:
            pickUpDate && dropOffDate ? daysCount(new Date(pickUpDate), new Date(dropOffDate)).toString() : '',
          TRAVEL_PASSENGERS: '1',
          TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
          SEARCHID: '',
          ERROR_MESSAGE: errors?.join(','),
        };
        await amplitudelogEvent(amplitudeEvents.TRAVEL_SEARCH_ERROR, event, accountResult);

        if (errors?.some((e: string) => e.toLowerCase().includes('no results'))) {
          const emptyData: VehicleSearchResponse = {
            vehicleOption: [],
            searchId: '',
            resultsAvailableUntil: dateToString(new Date()),
            resultsCount: 0,
          };
          dispatch(autosSearchSuccessful({ ...emptyData }));
          resolve(emptyData);
          return;
        }

        Sentry.addBreadcrumb({
          category: 'auto',
          message: 'Could not search autos',
          data: { ex, response: ex.response },
        });
        Sentry.captureMessage('[Autos] Could not search autos');
        dispatch(autosSearchError(ex));
        reject(ex);
      }
    });

export const autoResults = (newFilters?: VehicleResultsFilters, newSearchId?: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  try {
    const { accountResult } = getState().user;
    if (!accountResult) return;
    const { sessionToken, id: loggedUserId } = accountResult;

    const { filters, search, results, isEnd, isLoadingResults, traveler } = getState().autos;
    const currentOrderCriteria = filters?.orderCriteria;
    const currentOrderDesc = filters?.orderDesc;
    if (!search) return;
    const { searchId, resultsCount } = search;
    const distanceRestriction = getState().autos.search?.distanceRestriction ?? 'International';

    const maxResultsPerPage = Number(ENV.SEARCH_MAX_RESULT_PER_PAGE);

    if ((isEnd && !newFilters) || (!newFilters && results && resultsCount && results.length >= resultsCount)) {
      return;
    }
    if (isLoadingResults && (newFilters === undefined || newFilters === null)) return;

    if (isLoadingResults && cancel) cancel();

    dispatch(autoResultsRequest(newFilters));

    const resultCountLowerBound =
      !newFilters && results ? Math.ceil(results.length / maxResultsPerPage) * maxResultsPerPage : 0;
    const resultCountUpperBound =
      !newFilters && results ? resultCountLowerBound + maxResultsPerPage : maxResultsPerPage;

    const payload: VehicleResultsPayload = {
      userId: traveler ? traveler.id : loggedUserId,
      travelArrangerUserId: traveler && traveler.id !== loggedUserId ? loggedUserId : undefined,
      searchId: newSearchId ? newSearchId : searchId,
      resultCountLowerBound,
      resultCountUpperBound,
      orderCriteria:
        newFilters && newFilters.orderCriteria
          ? newFilters.orderCriteria
          : typeof currentOrderCriteria !== 'undefined'
            ? currentOrderCriteria
            : DEFAULT_ORDER,
      orderDesc:
        newFilters && newFilters.orderDesc
          ? newFilters.orderDesc
          : typeof currentOrderDesc !== 'undefined'
            ? currentOrderDesc
            : DEFAULT_ORDER_DESC,
      sessionToken,
      distanceRestriction,
      ...(newFilters || filters),
    };
    const {
      data: { succeeded, data, errors },
    } = await travel.post<VehicleResultsPayload, AxiosResponse<Response<VehicleResultsResponse>>>(
      'Vehicle/Results',
      payload,
      {
        cancelToken: new CancelToken((c) => {
          cancel = c;
        }),
      },
    );

    if (!succeeded) throw errors;

    dispatch(autoResultsSuccessful(data));
  } catch (ex) {
    Sentry.addBreadcrumb({
      category: 'auto',
      message: 'Could not get auto results',
      data: { ex, response: ex.response },
    });
    Sentry.captureMessage('[Autos] Could not get auto results');
    dispatch(autoResultsError(ex));
  }
};

export const autoAgencies = (model: string, company: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  try {
    const { accountResult } = getState().user;
    if (!accountResult) return;

    const { sessionToken, id: loggedUserId } = accountResult;

    const { filters, search, isLoadingAgencies, traveler } = getState().autos;
    const currentOrderCriteria = filters?.orderCriteria;
    const currentOrderDesc = filters?.orderDesc;
    if (!search) return;

    const distanceRestriction = getState().autos.search?.distanceRestriction ?? 'International';
    const { searchId } = search;

    const maxResultsPerPage = Number(ENV.SEARCH_MAX_RESULT_PER_PAGE);

    if (isLoadingAgencies) return;

    dispatch(autoAgenciesRequest(model, company));

    const {
      data: { succeeded, data, errors },
    } = await travel.post<VehicleResultsPayload, AxiosResponse<Response<VehicleResultsResponse>>>('Vehicle/Results', {
      UserId: traveler ? traveler.id : loggedUserId,
      TravelArrangerUserId: traveler && traveler.id !== loggedUserId ? loggedUserId : undefined,
      SearchId: searchId,
      ResultCountLowerBound: 0,
      ResultCountUpperBound: maxResultsPerPage,
      OrderCriteria: currentOrderCriteria || DEFAULT_ORDER,
      OrderDesc: currentOrderDesc || DEFAULT_ORDER_DESC,
      SessionToken: sessionToken,
      Model: [model],
      Company: [company],
      distanceRestriction,
    } as VehicleResultsPayload);

    if (!succeeded) throw errors;

    dispatch(autoAgenciesSuccessful(data));
  } catch (ex) {
    Sentry.addBreadcrumb({
      category: 'auto',
      message: 'Could not get auto results',
      data: { ex, response: ex.response },
    });
    Sentry.captureMessage('[Autos] Could not get auto results');
    dispatch(autoAgenciesError(ex));
  }
};

export const confirmAutoSolicitation = (
  auto: VehicleValidateResponse,
  justifyForBreakingRules: string,
  reason: string,
  detailId: string,
  validateId: string,
  optionId: string,
  breakRulesMessage: string[],
  validateResponse: TravelVehicleValidateResponse,
  costCenterId?: string,
  breakRulesMessageId?: string,
) => (dispatch: Dispatch, getState: () => RootState) =>
    new Promise<void>(async (resolve, reject) => {
      const { accountResult } = getState().user;
      const { search, traveler, branchCompany } = getState().autos;
      try {
        const { currency } = getState().i18n.country;

        if (!currency || !search || !accountResult || !auto || !auto.vehicleOption) return;

        const getDocumentId = (): number => {
          const doc = auto.validDocuments?.find((doc) => doc.name.includes('identidad'));
          if (doc) return doc.id;
          return 0;
        };

        let driver: VehicleBookDriver = {
          gender: `${accountResult.gender}`,
          firstName: accountResult.name,
          lastName: accountResult.lastName,
          phone: accountResult.phoneNumber,
          email: accountResult.email,
          nationality: accountResult.country,
          dob: moment(accountResult.birthDate).toISOString(),
          documentNumber: accountResult.documentNumber,
          configurationDocumentId: getDocumentId(),
        };

        let passenger;
        if (traveler) {
          passenger = {
            gender: traveler.gender,
            firstName: traveler.name,
            lastName: traveler.lastName,
            phone: traveler.phoneNumber,
            email: traveler.email,
            nationality: traveler.country,
            dob: moment(traveler.birthDate).toISOString(),
            documentNumber: traveler.documentNumber,
            passportNumber: traveler.passportNumber,
            paxType: 'Adult',
          };
          driver = {
            gender: `${traveler.gender}`,
            firstName: traveler.name,
            lastName: traveler.lastName,
            phone: traveler.phoneNumber,
            email: traveler.email,
            nationality: traveler.country,
            dob: moment(traveler.birthDate).toISOString(),
            documentNumber: traveler.documentNumber,
            configurationDocumentId: getDocumentId(),
          };
        }

        let vehicleBook: VehicleBook = {
          sessionToken: accountResult.sessionToken,
          searchid: search.searchId ? search.searchId : '',
          optionid: optionId,
          reservationId: 0,
          currency,
          travelDate: `${auto.vehicleOption.pickUp?.time}`,
          validateAvailableUntil: `${auto.vehicleOption.pickUp?.time}`,
          reservation: mapVehicleReservation(
            auto,
            breakRulesMessage ? breakRulesMessage : [],
            passenger ? [passenger] : [],
          ),
          driver,
          distanceRestriction: `${search.distanceRestriction}`,
          lowestLogicalFare: 0,
          paymentReferenceCode: '',
          detailId,
          validateId,
          travelItineraryId: 0,
          additionalInformation: '',
        };

        if (auto.vehicleOption.rates && auto.vehicleOption.rates[0]) {
          vehicleBook = {
            ...vehicleBook,
            price: { ...auto.vehicleOption.rates[0].price, breakRulesMessage: breakRulesMessage || [] },
            rateId: auto.vehicleOption.rates[0].rateId ? Number(auto.vehicleOption.rates[0].rateId) : 0,
          };
        }

        await travel.post('Travels/Create', {
          userId: traveler ? traveler.id || traveler.userId : accountResult.id,
          userFullName: traveler
            ? `${traveler.name} ${traveler.lastName}`
            : `${accountResult.name} ${accountResult.lastName}`,
          companyId: accountResult.companyId,
          currency,
          vehicleBook,
          justifyForBreakingRules,
          reason,
          costCenterId,
          travelArrangerUserId: traveler && traveler.id !== accountResult.id ? accountResult.id : undefined,
          validationId: validateResponse.validationId,
          documentId: validateResponse.documentId,
          breakRuleReasonId: breakRulesMessageId,
          billingCompanyId: branchCompany?.id,
          lowestLogicalFareList: search.lowestLogicalFareList,
        } as AutoSolicitationRequest);

        dispatch(autosClear());
        resolve();
      } catch (ex) {
        const pickUpPoint = auto.vehicleOption.pickUp?.destinationCode;
        const dropOffPoint = auto.vehicleOption.dropOff?.destinationCode;
        const pickUpDate = auto.vehicleOption.pickUp?.time;
        const dropOffDate = auto.vehicleOption.dropOff?.time;

        const event: TravelErrorEvent = {
          SOURCE: 'TRAVEL_HOME',
          RESULTS: 0,
          TRAVEL_PRODUCT_TYPE: 'CAR_RENTAL',
          TRAVEL_FROM: pickUpPoint ?? '',
          TRAVEL_TO: dropOffPoint ?? '',
          TRAVEL_DESTINATION_TYPE: '',
          TRAVEL_TRIP_TYPE: '',
          TRAVEL_ADVANCED_PURCHASE: pickUpDate ? daysCount(new Date(), new Date(pickUpDate)).toString() : '',
          TRAVEL_DATE_FROM: pickUpDate?.toString() ?? '',
          TRAVEL_DATE_TO: auto.vehicleOption.dropOff?.time?.toString() ?? '',
          TRAVEL_TRIP_LENGTH:
            pickUpDate && dropOffDate ? daysCount(new Date(pickUpDate), new Date(dropOffDate)).toString() : '',
          TRAVEL_PASSENGERS: '1',
          TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
          SEARCHID: search?.searchId ?? '',
          ERROR_MESSAGE: ex.toString(),
        };
        await amplitudelogEvent(amplitudeEvents.TRAVEL_BOOKING_ERROR, event, accountResult);

        reject(ex);
      }
    });

export const getFilters = () => async (dispatch: Dispatch, getState: () => RootState) =>
  new Promise<VehicleFilterResponse>(async (resolve, reject) => {
    try {
      const { accountResult } = getState().user;
      const { search } = getState().autos;

      const {
        data: { succeeded, data, errors },
      } = await travel.post<Response<VehicleFilterResponse>>('Vehicle/Filter', {
        //params: {
        //...axiosConfig,
        sessionToken: accountResult?.sessionToken || '',
        searchId: search?.searchId,
        //},
      });
      if (!succeeded) {
        throw errors;
      }
      const { filter } = data;
      dispatch(autosTabFilterSuccessful(filter));

      resolve(data);
    } catch (ex) {
      Sentry.addBreadcrumb({
        category: 'auto',
        message: 'Could not get auto filters',
        data: { ex, response: ex.response },
      });
      Sentry.captureMessage('[Autos] Could not get auto filters');
      reject(ex);
    }
  });

export const getAutoDetails = (searchId: string, sessionToken: string, optionId: number) => async (
  dispatch: Dispatch,
) =>
  new Promise<VehicleDetailResponse>(async (resolve, reject) => {
    try {
      dispatch(autoDetailsRequest(searchId, optionId));
      const {
        data: { succeeded, data, errors },
      } = await travel.post<VehicleDetailPayload, AxiosResponse<Response<VehicleDetailResponse>>>('Vehicle/Details', {
        searchId,
        sessionToken,
        optionId,
      } as VehicleDetailPayload);

      if (!succeeded) {
        throw errors;
      }

      dispatch(autoDetailsSuccess(data));
      resolve(data);
    } catch (ex) {
      Sentry.addBreadcrumb({
        category: 'auto',
        message: 'Could not get vehicle details',
        data: { ex, response: ex.response },
      });
      Sentry.captureMessage('[Autos] Could not get vehicle details');
      reject(ex);
    }
  });

export const getAutoValidate = (
  detailId: string,
  sessionToken: string,
  userId: string,
  distanceRestriction: string,
) => async (dispatch: Dispatch, getState: () => RootState) =>
    new Promise<VehicleValidateResponse>(async (resolve, reject) => {
      try {
        dispatch(autoValidateRequest(detailId));
        const { branchCompany } = getState().autos;
        const {
          data: { succeeded, data, errors },
        } = await travel.post<VehicleValidatePayload, AxiosResponse<Response<VehicleValidateResponse>>>(
          'Vehicle/Validate',
          {
            sessionToken,
            detailId,
            rateId: 1,
            isPayNow: true,
            userId,
            distanceRestriction,
            billingCompanyId: branchCompany?.id
          } as VehicleValidatePayload,
        );

        if (!succeeded) {
          throw errors;
        }

        dispatch(autoValidateSuccess(data));
        resolve(data);
      } catch (ex) {
        Sentry.addBreadcrumb({
          category: 'auto',
          message: 'Could not get vehicle validate',
          data: { ex, response: ex.response },
        });
        Sentry.captureMessage('[Autos] Could not get vehicle validate');
        reject(ex);
      }
    });

export const validateVehicle = (
  auto: VehicleValidateResponse,
  justifyForBreakingRules: string,
  reason: string,
  detailId: string,
  validateId: string,
  optionId: string,
  breakRulesMessage: string[],
  redux: RootState,
  costCenterId?: string,
) => new Promise<any>(async (resolve, reject) => {

  const { accountResult } = redux.user;
  const { search, traveler } = redux.autos;
  try {
    const { currency } = redux.i18n.country;

    if (!currency || !search || !accountResult || !auto || !auto.vehicleOption) return;

    const getDocumentId = (): number => {
      const doc = auto.validDocuments?.find((doc) => doc.name.includes('identidad'));
      if (doc) return doc.id;
      return 0;
    };

    let driver: VehicleBookDriver = {
      gender: `${accountResult.gender}`,
      firstName: accountResult.name,
      lastName: accountResult.lastName,
      phone: accountResult.phoneNumber,
      email: accountResult.email,
      nationality: accountResult.country,
      dob: moment(accountResult.birthDate).toISOString(),
      documentNumber: accountResult.documentNumber,
      configurationDocumentId: getDocumentId(),
    };

    let passenger;
    if (traveler) {
      passenger = {
        gender: traveler.gender,
        firstName: traveler.name,
        lastName: traveler.lastName,
        phone: traveler.phoneNumber,
        email: traveler.email,
        nationality: traveler.country,
        dob: moment(traveler.birthDate).toISOString(),
        documentNumber: traveler.documentNumber,
        passportNumber: traveler.passportNumber,
        paxType: 'Adult',
      };
      driver = {
        gender: `${traveler.gender}`,
        firstName: traveler.name,
        lastName: traveler.lastName,
        phone: traveler.phoneNumber,
        email: traveler.email,
        nationality: traveler.country,
        dob: moment(traveler.birthDate).toISOString(),
        documentNumber: traveler.documentNumber,
        configurationDocumentId: getDocumentId(),
      };
    }

    let vehicleBook: VehicleBook = {
      sessionToken: accountResult.sessionToken,
      searchid: search.searchId ? search.searchId : '',
      optionid: optionId,
      reservationId: 0,
      currency,
      travelDate: `${auto.vehicleOption.pickUp?.time}`,
      validateAvailableUntil: `${auto.vehicleOption.pickUp?.time}`,
      reservation: mapVehicleReservation(
        auto,
        breakRulesMessage ? breakRulesMessage : [],
        passenger ? [passenger] : [],
      ),
      driver,
      distanceRestriction: `${search.distanceRestriction}`,
      lowestLogicalFare: 0,
      paymentReferenceCode: '',
      detailId,
      validateId,
      travelItineraryId: 0,
      additionalInformation: '',
    };

    if (auto.vehicleOption.rates && auto.vehicleOption.rates[0]) {
      vehicleBook = {
        ...vehicleBook,
        price: { ...auto.vehicleOption.rates[0].price, breakRulesMessage: breakRulesMessage || [] },
        rateId: auto.vehicleOption.rates[0].rateId ? Number(auto.vehicleOption.rates[0].rateId) : 0,
      };
    }

    const data = await travel.post('Travels/Validate', {
      userId: traveler ? traveler.id || traveler.userId : accountResult.id,
      userFullName: traveler
        ? `${traveler.name} ${traveler.lastName}`
        : `${accountResult.name} ${accountResult.lastName}`,
      companyId: accountResult.companyId,
      currency,
      vehicleBook,
      justifyForBreakingRules,
      reason,
      costCenterId,
      travelArrangerUserId: traveler && traveler.id !== accountResult.id ? accountResult.id : undefined,
      lowestLogicalFareList: search.lowestLogicalFareList,
    } as AutoSolicitationRequest).then((response) => response.data);

    resolve(data);
  } catch (ex) {
    const pickUpPoint = auto.vehicleOption.pickUp?.destinationCode;
    const dropOffPoint = auto.vehicleOption.dropOff?.destinationCode;
    const pickUpDate = auto.vehicleOption.pickUp?.time;
    const dropOffDate = auto.vehicleOption.dropOff?.time;

    const event: TravelErrorEvent = {
      SOURCE: 'TRAVEL_HOME',
      RESULTS: 0,
      TRAVEL_PRODUCT_TYPE: 'CAR_RENTAL',
      TRAVEL_FROM: pickUpPoint ?? '',
      TRAVEL_TO: dropOffPoint ?? '',
      TRAVEL_DESTINATION_TYPE: '',
      TRAVEL_TRIP_TYPE: '',
      TRAVEL_ADVANCED_PURCHASE: pickUpDate ? daysCount(new Date(), new Date(pickUpDate)).toString() : '',
      TRAVEL_DATE_FROM: pickUpDate?.toString() ?? '',
      TRAVEL_DATE_TO: auto.vehicleOption.dropOff?.time?.toString() ?? '',
      TRAVEL_TRIP_LENGTH:
        pickUpDate && dropOffDate ? daysCount(new Date(pickUpDate), new Date(dropOffDate)).toString() : '',
      TRAVEL_PASSENGERS: '1',
      TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
      SEARCHID: search?.searchId ?? '',
      ERROR_MESSAGE: ex.toString(),
    };
    await amplitudelogEvent(amplitudeEvents.TRAVEL_BOOKING_ERROR, event, accountResult);

    reject(ex);
  }
});
